/**
 * EventReporter - Sends consent events to API (fire-and-forget)
 *
 * CRITICAL: This must NEVER block the page or throw errors.
 * All failures are silently caught.
 */

import type {
  ConsentPopInitConfig,
  ConsentEventPayload,
  WidgetInteractionPayload,
} from './types';
import { allowsConfiguredEdge } from './edge-routing';

declare const __CONSENTPOP_API_URL__: string;
declare const __CONSENTPOP_HEARTBEAT_API_URL__: string;
declare const __CONSENTPOP_DEBUG__: boolean;

export type WidgetRuntimeEvent =
  | {
      event: 'init_success';
      hostname: string;
      configVersion: number;
      themePreset: string;
      widgetVersion: string;
      source: 'network' | 'cache' | 'stale-cache';
    }
  | {
      event: 'config_error';
      hostname: string;
      errorCode: string;
    };

export class EventReporter {
  private apiKey: string;
  private apiUrl: string;
  private heartbeatUrl: string;
  private pending: Array<{ payload: ConsentEventPayload; attempts: number; createdAt: number }> = [];
  private sending = false;
  private additionalInFlight = new Set<(typeof this.pending)[number]>();
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly maxPending = 100;
  private readonly eventTtl = 24 * 60 * 60 * 1000;

  constructor(config: ConsentPopInitConfig) {
    this.apiKey = config.apiKey;
    this.apiUrl = this.resolveApiUrl(config.apiUrl);
    this.heartbeatUrl = allowsConfiguredEdge(config.apiUrl) && !this.apiUrl.startsWith('http://localhost') && !this.apiUrl.startsWith('http://127.0.0.1') &&
      typeof __CONSENTPOP_HEARTBEAT_API_URL__ !== 'undefined' && __CONSENTPOP_HEARTBEAT_API_URL__
      ? __CONSENTPOP_HEARTBEAT_API_URL__ : this.apiUrl;
    this.pending = this.readPending();
    if (this.pending.length > 0) queueMicrotask(() => { void this.flush().catch(() => {}); });
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.beaconPending());
    }
  }

  /**
   * Report a consent event to the API.
   * The visitor choice is already local. Reporting is asynchronous and never
   * delays the page; a stable ID lets the server deduplicate any retry.
   */
  report(payload: ConsentEventPayload): void {
    try {
      this.prunePending();
      const now = Date.now();
      const event = {
        payload: { ...payload, eventId: payload.eventId ?? this.createEventId(), occurredAt: payload.occurredAt ?? now },
        attempts: 0,
        createdAt: now,
      };
      this.pending.push(event);
      if (this.pending.length > this.maxPending) this.pending.shift();
      this.savePending();
      if (this.sending || this.retryTimer !== null) {
        // A previous failed request must not delay a visitor's new choice.
        this.additionalInFlight.add(event);
        void this.sendAdditional(event).catch(() => {});
      } else {
        void this.flush().catch(() => {});
      }
    } catch {
      // Silently ignore ALL errors — never break the customer's page
    }
  }

  private get storageKey(): string {
    return `cp_receipts_${this.apiKey}`;
  }

  private readPending(): typeof this.pending {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const now = Date.now();
      return parsed.filter((item) =>
        item && typeof item === 'object' &&
        typeof item.createdAt === 'number' && now - item.createdAt < this.eventTtl &&
        typeof item.attempts === 'number' && item.attempts >= 0 &&
        item.payload && typeof item.payload.eventId === 'string'
      ).slice(-this.maxPending);
    } catch {
      return [];
    }
  }

  private savePending(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.pending));
    } catch {
      // Storage can be disabled or full. The in-memory queue remains usable.
    }
  }

  private prunePending(): void {
    const now = Date.now();
    this.pending = this.pending.filter((event) =>
      now - event.createdAt < this.eventTtl);
    this.savePending();
  }

  private removePending(event: (typeof this.pending)[number]): void {
    const index = this.pending.indexOf(event);
    if (index !== -1) this.pending.splice(index, 1);
    this.savePending();
  }

  private async attempt(event: (typeof this.pending)[number]): Promise<Response | null> {
    event.attempts += 1;
    this.savePending();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      return await fetch(`${this.apiUrl}/api/consent/${this.apiKey}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event.payload), keepalive: true, signal: controller.signal,
      });
    } catch {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  private isTerminal(response: Response | null): boolean {
    return Boolean(response && (response.ok || (![408, 429].includes(response.status) && response.status < 500)));
  }

  private async sendAdditional(event: (typeof this.pending)[number]): Promise<void> {
    try {
      const response = await this.attempt(event);
      if (this.isTerminal(response)) this.removePending(event);
    } finally {
      this.additionalInFlight.delete(event);
      if (!this.sending && this.retryTimer === null) void this.flush().catch(() => {});
    }
  }

  private async flush(): Promise<void> {
    if (this.sending || this.retryTimer !== null) return;
    this.sending = true;
    try {
      while (this.pending.length > 0) {
        const event = this.pending[0];
        if (this.additionalInFlight.has(event)) return;
        if (Date.now() - event.createdAt >= this.eventTtl) {
          this.removePending(event);
          continue;
        }
        const response = await this.attempt(event);
        if (this.isTerminal(response)) {
          this.removePending(event);
          continue;
        }
        const retryAfter = response?.headers?.get('retry-after');
        const retrySeconds = retryAfter && /^\d+$/.test(retryAfter) ? Number(retryAfter) : null;
        const delay = retrySeconds !== null
          ? Math.max(1_000, Math.min(retrySeconds * 1000, 300_000))
          : Math.min(1000 * 2 ** Math.min(event.attempts - 1, 9) + Math.random() * 500, 300_000);
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          void this.flush().catch(() => {});
        }, delay);
        return;
      }
    } finally {
      this.sending = false;
    }
  }

  private beaconPending(): void {
    try {
      if (typeof navigator?.sendBeacon !== 'function') return;
      for (const event of this.pending) {
        navigator.sendBeacon(
          `${this.apiUrl}/api/consent/${this.apiKey}`,
          new Blob([JSON.stringify(event.payload)], { type: 'application/json' }),
        );
      }
      // A queued beacon is not a receipt acknowledgement. Keep items for retry.
    } catch {
      // Page unload must never affect the host.
    }
  }

  /**
   * Report a widget lifecycle interaction separately from a consent choice.
   * It intentionally has no visitor identifier, category list, or GCM state.
   */
  reportInteraction(payload: WidgetInteractionPayload): void {
    try {
      const body = JSON.stringify({
        ...payload,
        eventId: this.createEventId(),
        occurredAt: Date.now(),
      });
      const url = `${this.apiUrl}/api/widget/${this.apiKey}/interaction`;

      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) return;
      }

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {
        // Lifecycle analytics must never affect the customer page.
      });
    } catch {
      // Lifecycle analytics must never affect the customer page.
    }
  }

  getApiKey(): string {
    return this.apiKey;
  }

  /**
   * Report widget runtime state separately from a consent choice. This event
   * contains no visitor identifier or consent data and powers installation and
   * configuration-health status in the dashboard.
   */
  reportRuntime(payload: WidgetRuntimeEvent): void {
    try {
      const path = `/api/widget/${this.apiKey}/heartbeat`;
      const options: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      };
      const fallback = () => {
        if (this.heartbeatUrl !== this.apiUrl) void fetch(`${this.apiUrl}${path}`, options).catch(() => {});
      };
      void fetch(`${this.heartbeatUrl}${path}`, options)
        // A rolled-back Worker may not have the heartbeat route at all.
        .then((response) => { if (!response.ok && (response.status === 404 || response.status >= 500)) fallback(); })
        .catch(fallback);
    } catch {
      // Runtime diagnostics must never affect a customer's page.
    }
  }

  private resolveApiUrl(configUrl?: string): string {
    if (configUrl) return configUrl;

    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        return 'http://localhost:3000';
      }
    }

    if (typeof __CONSENTPOP_API_URL__ !== 'undefined') {
      return __CONSENTPOP_API_URL__;
    }

    return 'https://consentpop.com';
  }

  private createEventId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
}
